import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEnvelope, FaEnvelopeOpenText } from "react-icons/fa";
import { requestPasswordReset } from "../api/password-reset";
import { validateEmail } from "../utils/passwordValidation";
import { Button, Field, Input } from "../ui";
import AuthLayout, { AuthLink } from "../components/site/AuthLayout";
import { authCarry } from "../utils/authRedirect";

const PasswordResetRequest = () => {
  const location = useLocation();
  const carry = authCarry(location);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetInfo, setResetInfo] = useState(null);

  const handleResetRequest = async (e) => {
    e.preventDefault();

    // Validate email
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      toast.error(emailValidation.error);
      return;
    }

    setLoading(true);
    try {
      const response = await requestPasswordReset(email);

      if (response.success) {
        setResetSent(true);
        setResetInfo(response);
        toast.success(
          "تم إرسال تعليمات إعادة تعيين كلمة المرور إلى بريدك الإلكتروني"
        );
      } else {
        toast.error(
          response.message || "حدث خطأ أثناء إرسال طلب إعادة التعيين"
        );
      }
    } catch (error) {
      toast.error(error.message || "حدث خطأ أثناء إرسال طلب إعادة التعيين");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    setResetSent(false);
    setResetInfo(null);
  };

  const backToLogin = (
    <>
      تذكرت كلمة المرور؟{" "}
      <AuthLink to="/login" state={carry}>
        تسجيل الدخول
      </AuthLink>
    </>
  );

  if (resetSent) {
    return (
      <AuthLayout title="تحقق من بريدك" footer={backToLogin}>
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-success/15 text-success flex items-center justify-center mx-auto mb-5">
            <FaEnvelopeOpenText className="text-2xl" />
          </div>
          <p className="text-white/80">أرسلنا رابط إعادة تعيين كلمة المرور إلى:</p>
          <p dir="ltr" className="text-lg font-bold text-primary my-2 break-all">
            {email}
          </p>
          {resetInfo?.expiresIn && (
            <p className="text-sm text-white/60">
              الرابط صالح لمدة {resetInfo.expiresIn} دقيقة
            </p>
          )}
        </div>

        <div className="mt-6 rounded-xl bg-white/[0.05] border border-white/10 p-4 text-sm text-white/75">
          <p className="font-bold text-white mb-2">ما وصلك البريد؟</p>
          <ul className="list-disc pr-5 space-y-1">
            <li>تحقق من مجلد الرسائل غير المرغوب فيها</li>
            <li>تأكد من صحة عنوان البريد الإلكتروني</li>
            <li>انتظر بضع دقائق قبل المحاولة مرة أخرى</li>
          </ul>
        </div>

        <div className="mt-6 space-y-3">
          <Button block variant="secondary" onClick={handleResend}>
            إعادة الإرسال
          </Button>
          <Button block variant="ghost" to="/">
            العودة إلى الصفحة الرئيسية
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="إعادة تعيين كلمة المرور"
      subtitle="اكتب بريدك ونرسل لك رابطاً لتعيين كلمة مرور جديدة"
      footer={backToLogin}
    >
      <form onSubmit={handleResetRequest} className="space-y-5" noValidate>
        <Field label="البريد الإلكتروني" htmlFor="email">
          <Input
            ltr
            type="email"
            id="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="example@email.com"
            required
            disabled={loading}
          />
        </Field>

        <Button
          type="submit"
          size="lg"
          block
          loading={loading}
          disabled={!email}
          icon={<FaEnvelope />}
        >
          إرسال رابط إعادة التعيين
        </Button>

        <p className="text-xs text-white/55 text-center leading-relaxed">
          الرابط آمن وصالح لمدة 15 دقيقة فقط.
        </p>
      </form>
    </AuthLayout>
  );
};

export default PasswordResetRequest;
